import {
  type ReorderGanttInput,
  type AiSuggestionView,
  type DependencyView,
  type GanttView,
  type ScheduleImpactView,
  type TaskKind,
  type TaskGroupView,
  type TaskMutationResult,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

export const ganttApi = {
  /** `period`: kỳ của dự án bảo trì (APM-MAINT-e). Bỏ trống = không lọc thời gian. */
  view: (
    projectId: string,
    sprintId?: string | undefined,
    period?: string | null,
    offset?: number,
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams();
    if (sprintId !== undefined) params.set('sprintId', sprintId);
    if (period) params.set('period', period);
    if (offset !== undefined) {
      params.set('offset', String(offset));
      params.set('limit', '200');
    }
    const query = params.toString();
    return apiFetch<GanttView>(
      `/projects/${projectId}/gantt${offset === undefined ? '' : '/window'}${query ? `?${query}` : ''}`,
      ...(signal ? [{ signal }] : []),
    );
  },

  /**
   * Ảnh hưởng của lịch (nghỉ phép, nghỉ việc, cam kết, ngày lễ) — APM-1312.
   *
   * Query riêng, không nhập vào `view`: `GET .../gantt` chạy ở **mọi** cú kéo
   * thanh, còn câu trả lời này đọc thêm bốn bảng và đổi chậm hơn nhiều. Tách ra
   * cho Gantt vẽ trước rồi tô màu sau, thay vì chờ cả hai.
   */
  impact: (projectId: string) =>
    apiFetch<ScheduleImpactView>(`/projects/${projectId}/schedule-impact`),

  // ── Nhờ AI xếp lại lịch — APM-1313 ───────────────────────────────────────

  /**
   * Chạy skill qua cửa chung `/ai/skills/:name/run`, không qua endpoint riêng:
   * đó là chỗ duy nhất kiểm hạn mức và ghi `ai_usage_log` (ADR-006).
   */
  askReschedule: (projectId: string, prompt: string, runId?: string) =>
    apiFetch<AiSuggestionView>('/ai/skills/reschedule/run', {
      method: 'POST',
      body: { projectId, prompt, ...(runId ? { runId } : {}) },
    }),

  /**
   * Áp dụng **một phần** phương án. Trả lại cả Gantt: đổi người và dời ngày đều
   * có thể đổi đường găng của cả dự án.
   */
  applyReschedule: (projectId: string, suggestionId: string, indexes: number[]) =>
    apiFetch<GanttView>(`/projects/${projectId}/reschedule-suggestions/${suggestionId}/apply`, {
      method: 'POST',
      body: { indexes },
    }),

  /** Kéo thanh. Trả lại **cả** Gantt vì đường găng có thể đã đổi. */
  reschedule: (taskId: string, startDate: string, dueDate: string) =>
    apiFetch<GanttView>(`/tasks/${taskId}/schedule`, {
      method: 'PATCH',
      body: { startDate, dueDate },
    }),

  setKind: (taskId: string, kind: TaskKind) =>
    apiFetch<GanttView>(`/tasks/${taskId}/kind`, { method: 'PATCH', body: { kind } }),

  passGate: (taskId: string) =>
    apiFetch<GanttView>(`/tasks/${taskId}/pass-gate`, { method: 'POST' }),

  setBaseline: (projectId: string) =>
    apiFetch<GanttView>(`/projects/${projectId}/baseline`, { method: 'POST' }),

  /**
   * Nối hai task — APM-1305.
   *
   * Hai endpoint này trả về **cạnh**, không phải cả Gantt (khác `reschedule`),
   * nên người gọi phải tự nạp lại: thêm một phụ thuộc có thể đổi cả đường găng
   * và float của mọi task phía sau.
   */
  addDependency: (projectId: string, fromTaskId: string, toTaskId: string) =>
    apiFetch<DependencyView>(`/projects/${projectId}/dependencies`, {
      method: 'POST',
      body: { fromTaskId, toTaskId, type: 'finish_to_start', lagDays: 0 },
    }),

  /**
   * Xếp lại thứ tự hàng trên Gantt — APM-GANTT-h.
   *
   * `beforeTaskId = null` = thả xuống cuối. Server đánh số lại toàn bộ dự án
   * nên phải nạp lại cả Gantt, không chỉ một hàng.
   */
  reorder: (projectId: string, body: ReorderGanttInput) =>
    apiFetch<void>(`/projects/${projectId}/gantt/order`, { method: 'PATCH', body }),

  removeDependency: (dependencyId: string) =>
    apiFetch<void>(`/dependencies/${dependencyId}`, { method: 'DELETE' }),

  /**
   * Chuyển task vào **task cha** (subtask), hoặc `null` để đưa ra ngoài.
   *
   * Khác `assignGroup`: cái này là quan hệ cha–con của task (tầng 2, spec 9.1),
   * còn nhóm là một cái hộp có tên không phải task nào cả.
   */
  setParent: (taskId: string, parentId: string | null) =>
    apiFetch<TaskMutationResult>(`/tasks/${taskId}`, { method: 'PATCH', body: { parentId } }),

  // ── Nhóm việc (APM-1306) ─────────────────────────────────────────────────
  //
  // Cả bốn endpoint trả về **danh sách nhóm sau khi sửa**, nhưng Gantt vẫn phải
  // tải lại: thứ tự hàng do server xếp theo nhóm.

  /** Chỉ danh sách nhóm, không kéo theo cả bảng Gantt — dùng ở ô lọc nơi khác. */
  groups: (projectId: string) => apiFetch<TaskGroupView[]>(`/projects/${projectId}/task-groups`),

  createGroup: (projectId: string, name: string, taskIds: string[]) =>
    apiFetch<TaskGroupView[]>(`/projects/${projectId}/task-groups`, {
      method: 'POST',
      body: { name, taskIds },
    }),

  renameGroup: (groupId: string, name: string) =>
    apiFetch<TaskGroupView[]>(`/task-groups/${groupId}`, { method: 'PATCH', body: { name } }),

  deleteGroup: (groupId: string) =>
    apiFetch<TaskGroupView[]>(`/task-groups/${groupId}`, { method: 'DELETE' }),

  assignGroup: (projectId: string, taskIds: string[], groupId: string | null) =>
    apiFetch<TaskGroupView[]>(`/projects/${projectId}/tasks/group`, {
      method: 'PATCH',
      body: { taskIds, groupId },
    }),
};

export const ganttKeys = {
  /**
   * Khoá gồm cả sprint — APM-SPRINT-c/BOARD-b.
   *
   * Gộp mọi sprint vào một khoá thì đổi sprint sẽ hiện thanh của sprint cũ một
   * nhịp trước khi dữ liệu mới về, và người dùng đọc nó là "sprint này có việc
   * đó".
   */
  view: (projectId: string, sprintId?: string | undefined, period?: string | null) =>
    ['gantt', projectId, sprintId ?? null, period ?? null] as const,
  impact: (projectId: string) => ['gantt', projectId, 'impact'] as const,
  groups: (projectId: string) => ['gantt', projectId, 'groups'] as const,
};
